// Cruise Command · RC6-L01 · Opening Spread of Volume I
//
// Product Office selection (2026-08-09): Concept B — the visitor sees
// a magazine held open. Recto is the photograph; verso is the editorial
// page. No pricing, no SSO, no currency on the opening surface.
//
// When the visitor chooses "Begin Your Founding Edition", the verso
// composes to the authentication surface — the same spread, the same
// photograph, only the type page changes. Continuity is the point.
//
// Below the fold, the Manifest reinforces the emotional response the
// spread has already created. Pricing lives on its own surface after
// the traveller understands what Cruise Command is.
//
// LAW-001 · this component reads no product data. It composes into
// authentication only.
import React, { useMemo } from "react";
import { SsoProviders } from "@/components/auth/SsoProviders";
import { getSessionEditorialImage, backgroundPositionFor } from "@/lib/editorialImages";
import CandidateReviewPanel, { parseCandidateFromLocation } from "@/components/editorial/CandidateReviewPanel";
import { BRAND_ICON, BRAND_META } from "@/lib/BrandAssets";

// The verso ground — warm cream. Paper, not screen. Ink, not pixel.
const CREAM   = "#f5efe4";
const INK     = "#0b132b";
const EMBER   = "#ffd66b";
const ACCENT  = "#0f6a4d";  // warm slate-emerald for the kicker


export default function MarketingLanding({ onSignInRequested: _unused }) {
  // Candidate-preview mode — if the URL carries
  // `?editorial_candidate=...`, render the exact production
  // composition using the candidate photograph and overlay the
  // Product Office review panel. Otherwise render normally against
  // the session-sticky library.
  const candidate = useMemo(() => parseCandidateFromLocation(), []);

  // LAW-PHOTO-001 · one photograph per visitor session. Sticky
  // through refresh and route changes.  A future visit (fresh
  // session) may bring a different photograph.
  const heroImage = useMemo(
    () => candidate || getSessionEditorialImage(),
    [candidate]
  );

  return (
    <div
      data-testid="cc-marketing-landing"
      className="min-h-screen w-full bg-[#0b132b] overflow-x-hidden"
      style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
    >
      {/* ===== THE OPENING SPREAD =========================
          Verso (left) — the editorial page.
          Recto (right) — the photograph.
          One surface. One decision. One click to OAuth. */}
      <section
        data-testid="cc-marketing-spread"
        className="relative w-full md:min-h-screen flex flex-col md:flex-row"
      >
        {/* VERSO — the editorial identity and sign-in controls lead on
            mobile and remain on the left page at desktop widths. */}
        <div
          data-testid="cc-spread-verso"
          className="order-1 md:w-1/2 flex items-stretch cc-safe-x cc-safe-bottom"
          style={{ background: CREAM, color: INK }}
        >
          <div className="w-full max-w-[560px] mx-auto md:mx-0 md:ml-auto px-7 md:px-14 py-14 md:py-16 flex flex-col justify-start min-h-[80vh] md:min-h-screen">
            <OpeningSpread />
          </div>
        </div>

        {/* RECTO — the cruise photograph. On mobile it follows the
            editorial sign-in page; on desktop it remains on the right.
            PO 2026-02-14 · while the editorial library is empty
            (pending image ratification), the recto renders a pure
            navy fallback with no photograph — verified acceptable
            during the fallback audit. */}
        <div
          data-testid="cc-spread-recto"
          role="img"
          aria-label={heroImage?.alt || "Cruise Command editorial cover."}
          className="order-2 md:w-1/2 relative overflow-hidden bg-[#0b132b] cc-safe-top"
          style={{
            backgroundImage: heroImage?.url ? `url("${heroImage.url}")` : "none",
            backgroundSize: "cover",
            backgroundPosition: heroImage ? backgroundPositionFor(heroImage) : "center",
            minHeight: "56vh",
          }}
          data-testid-image="cc-marketing-hero-image"
          data-image-id={heroImage?.id || "none"}
          data-image-register={heroImage?.register || "none"}
        >
          {/* Duplicate the test-id required by regression tests, on an
              inline node — the recto element itself carries the image
              via CSS background. */}
          <span data-testid="cc-marketing-hero-image" className="sr-only">
            Editorial hero photograph.
          </span>

          {/* Recto photograph — no colophon overlay. The opening
              spread hides publication numbering from first-time
              visitors; the masthead lives on the verso only. */}
        </div>

        {/* Gutter — a soft vertical rule at the desktop fold that
            evokes the bound centre of the physical spread. Not shown
            on mobile (the two panels stack). */}
        <div
          aria-hidden
          className="hidden md:block absolute inset-y-0 left-1/2 -translate-x-1/2 w-6 pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.20) 45%, rgba(0,0,0,0.28) 50%, rgba(0,0,0,0.20) 55%, rgba(0,0,0,0) 100%)",
          }}
        />
      </section>

      {/* ===== BELOW THE FOLD — the Manifest reinforces desire ===== */}
      {/* RC7 · Section rewritten per Migration Inventory §19.15 —
          Binder-as-product language retired; Cruise Command reframed as
          the traveller's voyage operating system. */}
      <section id="the-voyage-os" className="max-w-6xl mx-auto px-7 md:px-12 py-24 border-t"
               style={{ borderColor: "rgba(245,239,228,0.10)", color: CREAM }}>
        <div className="text-[10px] font-black tracking-[0.28em] uppercase opacity-50 mb-4">
          Your Voyage Operating System
        </div>
        <h2 className="text-3xl md:text-4xl mb-10 max-w-3xl"
            style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 500 }}>
          One quiet companion, from the first idea to the last day.
        </h2>
        <div className="grid md:grid-cols-3 gap-10 mt-8">
          {[
            { k: "Before", body: "Plan the voyage in a single, unhurried workspace — ship, itinerary, ports, reservations, documents, dining, budget — organised the way an editor would compose an issue." },
            { k: "During", body: "The app travels with you. Ship time, deck plans, tickets, boarding documents, safety information — always to hand, working even when the ship's Wi-Fi does not." },
            { k: "After",  body: "Your memories stay yours — captured, revisited, and quietly ready when you begin planning the next voyage." },
          ].map((c) => (
            <div key={c.k}>
              <div className="text-[10px] font-black tracking-[0.28em] uppercase mb-3"
                   style={{ color: EMBER }}>{c.k}</div>
              <p className="opacity-80 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
        <p className="mt-16 opacity-60 italic max-w-2xl">
          "One tool. Every voyage. Composed with care."
        </p>
      </section>

      <footer
        data-testid="cc-marketing-footer"
        className="max-w-6xl mx-auto px-7 md:px-12 py-14 border-t"
        style={{ borderColor: "rgba(245,239,228,0.10)", color: CREAM }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-[10px] font-black tracking-[0.28em] uppercase opacity-40">
            Cruise Command · Voyage Intelligence · London · 2026
          </div>
          {/* Legal/compliance links — unobtrusive and editorial. */}
          <nav
            aria-label="Legal"
            className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] tracking-[0.14em] uppercase opacity-60"
          >
            <a
              href="https://mycruisecommand.com/legal/privacy.html"
              data-testid="cc-marketing-footer-privacy"
              className="hover:opacity-100 transition-opacity"
              style={{ color: CREAM }}
            >
              Privacy Policy
            </a>
            <a
              href="/account-deletion"
              data-testid="cc-marketing-footer-account-deletion"
              className="hover:opacity-100 transition-opacity"
              style={{ color: CREAM }}
            >
              Account &amp; Data Deletion
            </a>
          </nav>
        </div>
      </footer>

      {candidate && <CandidateReviewPanel candidate={candidate} />}
    </div>
  );
}


// ============================================================
// The single opening spread verso — editorial + authentication
// composed onto one surface.  One page. One decision. One click
// to OAuth.  (RC6-L04 · Product Office · 2026-08-09)
// ============================================================

function OpeningSpread() {
  return (
    <>
      {/* Canonical Cruise Command compass mark — sourced from
          `BrandAssets.BRAND_ICON.primary`. The masthead is the mark
          itself; the horizon rule and text kicker are retired here so
          the mark reads as the Volume-I cover signature. */}
      <img
        src={BRAND_ICON.primary}
        alt={BRAND_META.productName}
        data-testid="cc-marketing-masthead-logo"
        width={120}
        height={120}
        className="select-none"
        style={{ width: 120, height: 120, alignSelf: "flex-start" }}
        draggable={false}
      />

      {/* Headline — the sentence the spread was built for */}
      <h1
        data-testid="cc-marketing-h1"
        className="mt-4 leading-[1.02] tracking-tight"
        style={{
          fontFamily: "'Fraunces', Georgia, serif",
          fontWeight: 500,
          color: INK,
          fontSize: "clamp(2.5rem, 5vw, 4.5rem)",
        }}
      >
        Every voyage<br />
        deserves<br />
        its own edition.
      </h1>

      {/* Dek — one editorial sentence, no marketing, no software language */}
      <p
        data-testid="cc-marketing-dek"
        className="mt-8 max-w-md leading-relaxed"
        style={{ color: "#324058", fontSize: "16px", fontStyle: "italic" }}
      >
        Beautifully crafted travel editions, composed for one traveller,
        one voyage, kept for a lifetime.
      </p>

      {/* Connective line — the single line of editorial ceremony
          between reading and doing. */}
      <div className="mt-10 text-[10px] font-black tracking-[0.18em] uppercase"
           style={{ color: ACCENT }}
           data-testid="cc-marketing-cta-kicker">
        <span data-testid="cc-auth-sign-in-heading">SIGN IN OR CREATE YOUR ACCOUNT</span>
      </div>

      {/* One decision. One click to OAuth. */}
      <div className="mt-4 max-w-md" data-testid="cc-marketing-sso-block">
        <SsoProviders compact appearance="light" />
      </div>

      {/* Colophon foot */}
      <div className="mt-auto pt-14">
        <div className="text-[9.5px] font-black tracking-[0.18em] uppercase"
             style={{ color: "#7a8299" }} data-testid="cc-auth-version-copy">
          CRUISE COMMAND · VERSION 1.0 · 2026
        </div>
      </div>
    </>
  );
}
